import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Lightbulb, Clock, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CONFIDENCE_STYLES = {
  high: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  medium: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  low: 'bg-secondary text-muted-foreground border-border',
};

export function useScheduleRecommendations(token) {
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    fetch(`${API}/api/scheduled-calls/recommendations?tz=${encodeURIComponent(tz)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setRecs(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  return { recs, loading };
}

export function InlineTimeSuggestion({ recs, purpose }) {
  if (!recs) return null;
  const purposeRec = recs.by_purpose?.[purpose || 'follow_up'];
  const text = purposeRec?.best_times || recs.overall?.suggestion;
  if (!text) return null;

  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 bg-violet-500/5 border border-violet-500/15 rounded-md" data-testid="inline-time-suggestion">
      <Lightbulb size={11} className="text-violet-400 shrink-0" />
      <span className="text-[10px] text-violet-300">{text}</span>
    </div>
  );
}

export function ScheduleSuggestionsCard({ recs, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  if (!recs) return null;

  const { overall, insights, by_purpose, confidence, has_enough_data, total_calls_analyzed } = recs;

  if (compact) {
    return (
      <div className="bg-violet-500/5 border border-violet-500/15 rounded-lg p-3" data-testid="sched-suggestions-compact">
        <div className="flex items-center gap-2 mb-1.5">
          <Lightbulb size={13} className="text-violet-400" />
          <span className="text-xs font-medium text-violet-300">Smart Scheduling</span>
          <Badge className={`text-[9px] ${CONFIDENCE_STYLES[overall?.confidence || 'low']}`}>
            {overall?.confidence || 'low'} confidence
          </Badge>
        </div>
        <p className="text-xs text-foreground">{overall?.suggestion}</p>
        {insights?.[0] && <p className="text-[10px] text-muted-foreground mt-1">{insights[0]}</p>}
      </div>
    );
  }

  return (
    <div className="border border-violet-500/15 rounded-xl bg-gradient-to-b from-violet-500/5 to-transparent" data-testid="sched-suggestions-full">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-violet-500/10">
              <Lightbulb size={15} className="text-violet-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-foreground">Smart Scheduling</h3>
              <p className="text-[10px] text-muted-foreground">
                {has_enough_data
                  ? `Based on ${total_calls_analyzed} calls`
                  : 'Default recommendations — more data needed'}
              </p>
            </div>
          </div>
          <Badge className={`text-[9px] ${CONFIDENCE_STYLES[confidence || overall?.confidence || 'low']}`}>
            {confidence || overall?.confidence || 'low'}
          </Badge>
        </div>

        {/* Best time card */}
        <div className="bg-muted rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={12} className="text-teal-400" />
            <span className="text-xs font-medium text-teal-400">Best Overall Time</span>
          </div>
          <p className="text-sm text-foreground">{overall?.suggestion}</p>
          {overall?.answer_rate > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <TrendingUp size={10} className="text-emerald-400" />
              <span className="text-[10px] text-emerald-400">{overall.answer_rate}% connection rate</span>
            </div>
          )}
          {overall?.best_combos?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {overall.best_combos.slice(0, 3).map((c, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 bg-teal-500/10 text-teal-400 rounded-full">
                  {c.day} {c.hour} ({c.success_rate}%)
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Insights */}
        {insights?.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {insights.slice(0, expanded ? 5 : 2).map((insight, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-foreground">
                <span className="text-violet-400 mt-0.5 shrink-0">&#8226;</span>
                <span>{insight}</span>
              </div>
            ))}
          </div>
        )}

        {/* Purpose breakdown - expandable */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          data-testid="toggle-purpose-breakdown"
        >
          {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          {expanded ? 'Less detail' : 'Show by purpose'}
        </button>

        {expanded && by_purpose && (
          <div className="mt-2 space-y-1.5">
            {Object.entries(by_purpose).map(([p, data]) => (
              <div key={p} className="flex items-center justify-between bg-muted rounded-md px-2.5 py-1.5" data-testid={`purpose-rec-${p}`}>
                <span className="text-xs text-foreground capitalize">{p.replace(/_/g, ' ')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{data.best_times}</span>
                  <Badge className={`text-[8px] ${CONFIDENCE_STYLES[data.confidence || 'low']}`}>
                    {data.confidence || 'low'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
